// client/src/routes/portal/incidents/index.tsx — SCR-PORT-004 „Incidente" · PORTAL-002 phase 2.
//
// §8.16 gives this one the LIST template and makes it the authenticated twin of SCR-LINK-002.
//
// The report form is an overlay on the SAME route, held open by `?report=true` rather than by
// component state — so „Raportează un incident" is a link a landlord can send, Back closes the
// form instead of leaving the portal, and a reload does not discard a half-typed description.
// The same choice SCR-EXP-003 made for its builder (D-750), for the same reason; here the form
// REPLACES the list rather than sitting over it in a `<dialog>`, which is the call D-1190 made
// for SCR-OAT-001's cost form: the sticky action bar of §11.2 is `fixed` to the viewport, and
// inside a dialog's top layer that bar lands over the page instead of over the form.

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';
import { PageHeader } from '@/components/layout/PageHeader';
import { ContractScope } from '@/features/portal/ContractScope';
import { IncidentList, IncidentReportForm } from '@/features/portal/Incidents';
import { PortalSkeleton } from '@/features/portal/PortalSkeletons';
import { portalIncidentsSearchSchema } from '@/features/portal/search';
import { urlBoolean } from '@/lib/url-state';

export const Route = createFileRoute('/portal/incidents/')({
  validateSearch: portalIncidentsSearchSchema,
  component: PortalIncidentsScreen,
});

function PortalIncidentsScreen() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const reporting = urlBoolean(search.report) ?? false;

  const close = () => {
    void navigate({ search: (previous) => ({ ...previous, report: undefined }) });
  };

  /**
   * The list's own filters, written back into the URL — §9.2.3's rule for every LIST screen, and
   * here the URL and the request carry the same four values, because the narrowing is the
   * server's. `contract` is untouched: it belongs to the `/portal` layout route and the reducer
   * spreads whatever is already there.
   */
  const setSearch = (next: (previous: typeof search) => typeof search) => {
    // `resetScroll: false` so the PAGER owns the scroll. `main.tsx` creates the
    // router with `scrollRestoration: true`, which jumps the page to the top the
    // instant a search param changes — measured at 216 px to 0 between two 50 ms
    // samples — and that jump beat the pager's smooth scroll every time. The router
    // no longer moves the page on these navigations; `PortalPager` animates it
    // instead, and a filter change simply leaves the reader where they were
    // (D-1674).
    void navigate({ search: (previous) => next(previous), resetScroll: false });
  };

  return (
    <>
      <PageHeader
        title={t('portal.incidents.title')}
        description={t('portal.incidents.subtitle')}
        actions={
          reporting ? undefined : (
            <button
              type="button"
              className="btn btn-primary min-h-11"
              onClick={() => {
                // The string spelling, because that is what the router writes back into the URL
                // and what a shared link then parses (D-1197); `urlBoolean` reads both.
                void navigate({ search: (previous) => ({ ...previous, report: 'true' }) });
              }}
            >
              <Icon name="exclamation-triangle" className="h-4 w-4" />
              {t('portal.incidents.report')}
            </button>
          )
        }
      />

      {reporting ? (
        <ContractScope pending={<PortalSkeleton shape="rows" count={3} className="portal-card" />}>
          {(contract) => <IncidentReportForm contract={contract} onDone={close} />}
        </ContractScope>
      ) : (
        <IncidentList search={search} onSearchChange={setSearch} />
      )}
    </>
  );
}
